/**
 * Вкладка «Чаты»: строка каждого чата — состояние, что делает, последний запуск, что дальше, ветка — и сколько у него
 * открытых задач и взятых в работу. Строки пишут сами чаты (`node scripts/board.mjs chat … --chat <код>`).
 */
import { BOARD_CHAT_STATE_TITLES, BOARD_LIST_MAX, type BoardChat, type BoardTaskList } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api } from '../../lib/api';
import { dateTime, relative } from '../../lib/format';
import { Badge, EmptyState, ErrorBox, Spinner, type Tone } from '../../ui/feedback';
import { Panel } from '../../ui/layout';
import { BOARD_API } from './access';

const STATE_TONE: Record<BoardChat['state'], Tone> = {
  active: 'success',
  waiting: 'warning',
  paused: 'neutral',
};

export function ChatsTab({ onChat }: { onChat: (code: string) => void }) {
  const chats = useQuery({
    queryKey: ['devboard', 'chats'],
    queryFn: () => api<BoardChat[]>(`${BOARD_API}/chats`),
    refetchInterval: 60_000,
  });
  // Счёт по открытым задачам — до предела сервера; если открытых больше, под списком об этом сказано
  const open = useQuery({
    queryKey: ['devboard', 'tasks', { status: 'open', limit: BOARD_LIST_MAX }],
    queryFn: () =>
      api<BoardTaskList>(`${BOARD_API}/tasks`, { query: { status: 'open', limit: BOARD_LIST_MAX } }),
  });
  const counts = useMemo(() => {
    const m = new Map<string, { open: number; taken: number }>();
    for (const t of open.data?.tasks ?? []) {
      for (const code of new Set([t.chat, t.takenByChat].filter((x): x is string => Boolean(x)))) {
        const c = m.get(code) ?? { open: 0, taken: 0 };
        if (t.chat === code) c.open++;
        if (t.takenByChat === code) c.taken++;
        m.set(code, c);
      }
    }
    return m;
  }, [open.data]);

  if (chats.isPending) return <Spinner />;
  if (chats.isError) return <ErrorBox error={chats.error} />;
  if (!chats.data.length)
    return (
      <EmptyState title="Строк чатов пока нет">
        Чат заводит свою строку в начале запуска: node scripts/board.mjs --chat &lt;код&gt; chat &lt;код&gt;
        --status-line «…» --last-run now. Строки прежней доски появятся после переноса (вкладка «Доступ»).
      </EmptyState>
    );
  return (
    <Panel title="Чаты разработки" meta={`${chats.data.length}`} bodyClassName="p-0">
      <ul className="divide-y divide-line">
        {chats.data.map((c) => {
          const n = counts.get(c.code);
          return (
            <li
              key={c.code}
              className="flex flex-col gap-1.5 px-4 py-3 md:grid md:grid-cols-[220px_minmax(0,1fr)_200px] md:gap-4 md:px-5"
            >
              <div className="flex min-w-0 flex-col gap-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => onChat(c.code)}
                    className="inline-flex min-h-11 items-center text-[15px] font-semibold text-ink underline-offset-2 hover:underline md:min-h-0"
                    title="Задачи этого чата"
                  >
                    {c.code}
                  </button>
                  <Badge tone={STATE_TONE[c.state]}>{BOARD_CHAT_STATE_TITLES[c.state]}</Badge>
                </div>
                {c.name ? <span className="text-[13px] text-ink-2">{c.name}</span> : null}
                {n ? (
                  <span className="text-[12px] text-ink-2">
                    открытых {n.open} · взял {n.taken}
                  </span>
                ) : null}
              </div>
              <div className="flex min-w-0 flex-col gap-1 text-[14px] leading-5">
                <span className="[overflow-wrap:anywhere] text-ink">{c.statusLine ?? '—'}</span>
                {c.next ? (
                  <span className="[overflow-wrap:anywhere] text-[13px] text-ink-2">Дальше: {c.next}</span>
                ) : null}
                {c.area ? (
                  <span className="[overflow-wrap:anywhere] text-[13px] text-ink-2">Область: {c.area}</span>
                ) : null}
              </div>
              <div className="flex min-w-0 flex-col gap-1 text-[13px] text-ink-2">
                <span title={c.lastRun ? dateTime(c.lastRun) : undefined}>
                  Запуск: {c.lastRun ? relative(c.lastRun) : 'не было'}
                </span>
                {c.branch ? <span className="[overflow-wrap:anywhere]">{c.branch}</span> : null}
                <span>
                  Отметка: {c.updatedByChat ? `чат ${c.updatedByChat}` : (c.updatedByName ?? '—')},{' '}
                  {relative(c.updatedAt)}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
      {open.data?.truncated ? (
        <p className="border-t border-line px-4 py-3 text-[13px] text-ink-2 md:px-5">
          Счёт задач — по первым {open.data.tasks.length} открытым из {open.data.total}.
        </p>
      ) : null}
    </Panel>
  );
}
